import { useState, useMemo, useEffect, useRef } from "react";
import { seriesOptions, variantOptions } from "../constants/closetOptions";

export function useClosetDesign() {
  // ── Collection & Color ──
  const [collectionList, setCollectionList] = useState("");
  const [selectedColor, setSelectedColor] = useState("");

  // ── Doors ──
  const [closetCollection, setClosetCollection] = useState("");
  const [isSameAsCollectionColor, setIsSameAsCollectionColor] = useState(false);
  const [doorEnabled, setDoorEnabled] = useState();
  const [doorQuantity, setDoorQuantity] = useState();
  const [doorDecoStyle, setDoorDecoStyle] = useState("");
  const [doorSelectedSeries, setDoorSelectedSeries] = useState("");
  const [doorSelectedVariant, setDoorSelectedVariant] = useState("");
  const [doorColor, setDoorColor] = useState("");
  const [doorgripStyle, setDoorgripStyle] = useState("");
  const [doorgripSelectedSeries, setDoorgripSelectedSeries] = useState("");
  const [doorgripSelectedSize, setDoorgripSelectedSize] = useState("");
  const [doorgripColor, setDoorgripColor] = useState("");

  // ── Special Doors ──
  const [specialDoorEnabled, setSpecialDoorEnabled] = useState();
  const [specialDoorgripStyle, setSpecialDoorgripStyle] = useState("");
  const [specialDoorgripSelectedSeries, setSpecialDoorgripSelectedSeries] = useState("");
  const [specialDoorgripSelectedSize, setSpecialDoorgripSelectedSize] = useState("");
  const [specialDoorgripColor, setSpecialDoorgripColor] = useState("");
  const [specialDoorQuantity, setSpecialDoorQuantity] = useState("");
  const [specialDoorType, setSpecialDoorType] = useState("");
  const [specialDoorSize, setSpecialDoorSize] = useState("");
  const [specialDoorHeight, setSpecialDoorHeight] = useState("");
  const [specialDoorInsert, setSpecialDoorInsert] = useState("");
  const [specialDoorFrame, setSpecialDoorFrame] = useState("");
  const [specialDoorEdge, setSpecialDoorEdge] = useState("");
  const [specialDoorSectionError, setSpecialDoorSectionError] = useState(false);
  const [specialDoorFieldErrors, setSpecialDoorFieldErrors] = useState({});

  // ── Drawers ──
  const [closetColl, setClosetColl] = useState("");
  const [isSameAsCollColor, setIsSameAsCollColor] = useState(false);
  const [drawerEnabled, setDrawerEnabled] = useState();
  const [drawerQuantity, setDrawerQuantity] = useState();
  const [drawerDecoStyle, setDrawerDecoStyle] = useState("");
  const [drawerSelectedSeries, setDrawerSelectedSeries] = useState("");
  const [drawerSelectedVariant, setDrawerSelectedVariant] = useState("");
  const [drawerColor, setDrawerColor] = useState("");
  const [frontclosetColl, setFrontClosetColl] = useState("");
  const [isFrontSameAsCollColor, setIsFrontSameAsCollColor] = useState(false);
  const [drawerFrontColor, setDrawerFrontColor] = useState("");
  const [drawergripStyle, setDrawergripStyle] = useState("");
  const [drawergripSelectedSeries, setDrawergripSelectedSeries] = useState("");
  const [drawergripSelectedSize, setDrawergripSelectedSize] = useState("");
  const [drawergripColor, setDrawergripColor] = useState("");

  // ── Rods ──
  const [rodEnabled, setRodEnabled] = useState();
  const [rodQuantity, setRodQuantity] = useState();
  const [rodStyle, setRodStyle] = useState("");
  const [rodColor, setRodColor] = useState("");

  // ── Moulding ──
  const [mouldingStyle, setMouldingStyle] = useState("");
  const [noTopFinish, setNoTopFinish] = useState();
  const [moulding1Style, setMoulding1Style] = useState("");
  const [nobottomFinish, setNoBottomFinish] = useState();

  // ── Baseboard & Other Details ──
  const [baseboardRemove, setBaseboardRemove] = useState(false);
  const [baseboardNotch, setBaseboardNotch] = useState(false);
  const [backing, setBacking] = useState();
  const [drilling, setDrilling] = useState();
  const [drillingtxt, setDrillingtxt] = useState("");
  const drillingChangeRef = useRef(null);
  const drillingCursorPosRef = useRef(0);
  const [drillingNotesError, setDrillingNotesError] = useState(false);

  // ── Countertop ──
  const [countertopEnabled, setCountertopEnabled] = useState();
  const [countertopType, setCountertopType] = useState("");
  const [countertopColor, setCountertopColor] = useState("");
  const [countertopCollection, setCountertopCollection] = useState("");

  // ── Additional Notes ──
  const [additionalNotes, setAdditionalNotes] = useState("");

  // ── Derived: deco style options based on color ──
  const decoStyleMenuItems = useMemo(() => {
    if (!selectedColor) return ["Contemporary"];
    if (
      [
        "White - L191", "Ivory - L204", "Shadows Grey - L201",
        "Hardrock Maple - L303", "Candle Light - L421",
      ].includes(selectedColor)
    ) {
      return ["Deco", "Contemporary"];
    } else if (["White Chocolate - L492", "Winter Fun - M2004"].includes(selectedColor)) {
      return ["5-Piece", "Deco", "Contemporary"];
    } else if (
      [
        "Sheer Beauty - L581", "Fashionista - L582", "Sunset Cruise - M2002",
        "After-Hours - M2010", "First Class - L583", "Free Spirit - L580",
      ].includes(selectedColor)
    ) {
      return ["5-Piece", "Contemporary"];
    } else {
      return ["Contemporary"];
    }
  }, [selectedColor]);

  // ── Derived: moulding options based on color ──
  const moldingMenuItems = useMemo(() => {
    if (!selectedColor) return { noTopFinish: [], noBottomFinish: [] };
    if (
      [
        "White - L191", "Ivory - L204", "Shadows Grey - L201",
        "Hardrock Maple - L303", "Candle Light - L421",
        "White Chocolate - L492", "Winter Fun - M2004",
      ].includes(selectedColor)
    ) {
      return {
        noTopFinish: [
          "Decorative Crown", "Decorative Crown To Ceiling",
          "Contemporary Riser", "Contemporary to Ceiling", "Top Accent Shelf",
        ],
        noBottomFinish: ["Deco Base", "Contemporary Base"],
      };
    }
    return {
      noTopFinish: ["Contemporary Riser", "Contemporary to Ceiling", "Top Accent Shelf"],
      noBottomFinish: ["Contemporary Base"],
    };
  }, [selectedColor]);

  // ── Derived: door/drawer series & variant options ──
  const availableDoorSeries = useMemo(() => seriesOptions[doorDecoStyle] || [], [doorDecoStyle]);
  const availableDoorVariants = useMemo(() => variantOptions[doorSelectedSeries] || [], [doorSelectedSeries]);
  const availableDrawerSeries = useMemo(() => seriesOptions[drawerDecoStyle] || [], [drawerDecoStyle]);
  const availableDrawerVariants = useMemo(() => variantOptions[drawerSelectedSeries] || [], [drawerSelectedSeries]);

  // ── EFFECT: Door/drawer series-variant sync — resets variant when deco style changes ──
  useEffect(() => {
    const updateSelection = (decoStyle, setSeries, setVariant, available) => {
      if (!decoStyle || decoStyle === "Contemporary") {
        setSeries("");
        setVariant("");
      } else if (decoStyle === "5-Piece") {
        if (available.length === 1) setSeries(available[0]);
        setVariant("");
      }
    };
    updateSelection(doorDecoStyle, setDoorSelectedSeries, setDoorSelectedVariant, availableDoorSeries);
    updateSelection(drawerDecoStyle, setDrawerSelectedSeries, setDrawerSelectedVariant, availableDrawerSeries);
  }, [doorDecoStyle, availableDoorSeries, drawerDecoStyle, availableDrawerSeries]);

  // ── EFFECT: Reset variant when series changes ──
  useEffect(() => {
    const resetVariant = (decoStyle, setVariant, selectedVariant, available) => {
      if (decoStyle === "5-Piece" || !available.includes(selectedVariant)) {
        setVariant("");
      }
    };
    resetVariant(doorDecoStyle, setDoorSelectedVariant, doorSelectedVariant, availableDoorVariants);
    resetVariant(drawerDecoStyle, setDrawerSelectedVariant, drawerSelectedVariant, availableDrawerVariants);
  }, [
    doorSelectedSeries, availableDoorVariants, doorSelectedVariant, doorDecoStyle,
    drawerSelectedSeries, availableDrawerVariants, drawerSelectedVariant, drawerDecoStyle,
  ]);

  // ── EFFECT: Restore cursor position after drilling notes update ──
  useEffect(() => {
    if (drillingChangeRef.current) {
      drillingChangeRef.current.focus();
      drillingChangeRef.current.setSelectionRange(
        drillingCursorPosRef.current,
        drillingCursorPosRef.current
      );
    }
  }, [drillingtxt]);

  const handleDrillingChangeNotes = (event) => {
    const newValue = event.target.value;
    drillingCursorPosRef.current = event.target.selectionStart;
    setDrillingtxt(newValue);
  };

  return {
    // Collection & Color
    collectionList, setCollectionList,
    selectedColor, setSelectedColor,
    // Doors
    closetCollection, setClosetCollection,
    isSameAsCollectionColor, setIsSameAsCollectionColor,
    doorEnabled, setDoorEnabled,
    doorQuantity, setDoorQuantity,
    doorDecoStyle, setDoorDecoStyle,
    doorSelectedSeries, setDoorSelectedSeries,
    doorSelectedVariant, setDoorSelectedVariant,
    doorColor, setDoorColor,
    doorgripStyle, setDoorgripStyle,
    doorgripSelectedSeries, setDoorgripSelectedSeries,
    doorgripSelectedSize, setDoorgripSelectedSize,
    doorgripColor, setDoorgripColor,
    // Special Doors
    specialDoorEnabled, setSpecialDoorEnabled,
    specialDoorgripStyle, setSpecialDoorgripStyle,
    specialDoorgripSelectedSeries, setSpecialDoorgripSelectedSeries,
    specialDoorgripSelectedSize, setSpecialDoorgripSelectedSize,
    specialDoorgripColor, setSpecialDoorgripColor,
    specialDoorQuantity, setSpecialDoorQuantity,
    specialDoorType, setSpecialDoorType,
    specialDoorSize, setSpecialDoorSize,
    specialDoorHeight, setSpecialDoorHeight,
    specialDoorInsert, setSpecialDoorInsert,
    specialDoorFrame, setSpecialDoorFrame,
    specialDoorEdge, setSpecialDoorEdge,
    specialDoorSectionError, setSpecialDoorSectionError,
    specialDoorFieldErrors, setSpecialDoorFieldErrors,
    // Drawers
    closetColl, setClosetColl,
    isSameAsCollColor, setIsSameAsCollColor,
    drawerEnabled, setDrawerEnabled,
    drawerQuantity, setDrawerQuantity,
    drawerDecoStyle, setDrawerDecoStyle,
    drawerSelectedSeries, setDrawerSelectedSeries,
    drawerSelectedVariant, setDrawerSelectedVariant,
    drawerColor, setDrawerColor,
    frontclosetColl, setFrontClosetColl,
    isFrontSameAsCollColor, setIsFrontSameAsCollColor,
    drawerFrontColor, setDrawerFrontColor,
    drawergripStyle, setDrawergripStyle,
    drawergripSelectedSeries, setDrawergripSelectedSeries,
    drawergripSelectedSize, setDrawergripSelectedSize,
    drawergripColor, setDrawergripColor,
    // Rods
    rodEnabled, setRodEnabled,
    rodQuantity, setRodQuantity,
    rodStyle, setRodStyle,
    rodColor, setRodColor,
    // Moulding
    mouldingStyle, setMouldingStyle,
    noTopFinish, setNoTopFinish,
    moulding1Style, setMoulding1Style,
    nobottomFinish, setNoBottomFinish,
    // Baseboard & Other Details
    baseboardRemove, setBaseboardRemove,
    baseboardNotch, setBaseboardNotch,
    backing, setBacking,
    drilling, setDrilling,
    drillingtxt, setDrillingtxt,
    drillingChangeRef,
    drillingNotesError, setDrillingNotesError,
    handleDrillingChangeNotes,
    // Countertop
    countertopEnabled, setCountertopEnabled,
    countertopType, setCountertopType,
    countertopColor, setCountertopColor,
    countertopCollection, setCountertopCollection,
    // Additional Notes
    additionalNotes, setAdditionalNotes,
    // Derived values
    decoStyleMenuItems,
    moldingMenuItems,
  };
}
